import { defineCopy } from '@floorfile/i18n'

/**
 * User-facing text for API failure CODES.
 *
 * The API is unchanged and stays unchanged: `AppError('NOT_FOUND', 'Work order
 * not found.')` keeps its English `message`, because that string is a developer
 * artefact — it goes to Sentry, to `audit_log`, and into the OpenAPI document,
 * and none of those readers is the person holding the tablet. What the user
 * sees is keyed on the stable `code`, which is already part of the API contract
 * (`PLATFORM_ERROR_CODES`) and already exhaustively enumerated.
 *
 * Only the PLATFORM codes live here — the ones every module can raise. The
 * ~40 module-specific business codes (`NC_HAS_OPEN_HOLD`,
 * `APPROVAL_INCOMPLETE`, `METER_READING_REGRESSED`…) belong in their owning
 * feature's catalogue, where the person writing the rule is the person writing
 * the sentence. Until a module adds them, `translateErrorCode` falls back to
 * the server's English message, which for a business rule is precise and
 * actionable — losing that to show a generic apology in the right language
 * would be the worse trade.
 *
 * The namespace is `errors` because the directory is `errors`; the locale
 * loader derives it from the path.
 */
export const errorCopy = defineCopy('errors', {
  /** The floor for anything with no code at all — a transport failure, a
   * malformed envelope, a 500 with no body. Never blank, never silent. */
  unknown: 'Something went wrong and your change was not saved.',
  /** The toast's close control. */
  dismiss: 'Dismiss error',
  /** FF-AUDIT-099: the success note's close control. */
  dismissNote: 'Dismiss',
  /** FF-QA-144: the API request id under an error, for a support report. */
  reference: 'Ref: {id}',

  VALIDATION_FAILED: 'Some of the details are not valid. Check the highlighted fields and try again.',
  /** FF-QA-052: the client-side upload refusals (`UploadValidationError.code`). */
  UPLOAD_EMPTY: 'That file is empty and cannot be uploaded.',
  UPLOAD_TOO_LARGE: 'That file is too large to upload.',
  UPLOAD_NAME_TOO_LONG: 'That file name is too long.',
  UPLOAD_TYPE_NOT_ALLOWED: 'That file type is not allowed.',
  /** FF-AUDIT-041 */
  DOWNTIME_COST_NEGATIVE: 'A cost cannot be negative.',
  /** Owner decision 2026-09-18: Settings → Checks → Late completion is LOCK and the grace window has passed. */
  CHECK_LATE_LOCKED: 'This check is past its deadline and is recorded as missed. It can no longer be completed.',
  /** Owner decision 2026-09-30: under Lock, a check past its final deadline is closed. */
  CHECK_PAST_DEADLINE: 'This check is past its deadline and is closed. It can no longer be reopened.',
  /** FF-AUDIT-048 */
  AUDIT_NOT_YOUR_AUDIT: 'Only the named auditor can answer or complete this audit. Take ownership first.',
  /** F8.3 — another member of the team started it first. */
  AUDIT_ALREADY_CLAIMED: 'Someone else on the team has already taken this audit.',
  CHECK_ALREADY_CLAIMED: 'Someone else on the team has already taken this check.',
  DOWNTIME_RESOLUTION_EMPTY: 'Record the root cause or the action taken before resolving an unplanned stop.',
  /** FF-AUDIT-005: a form answer the server refused. The renderer marks the
   * question and names the rule; this is the banner above it. */
  FIELD_VALIDATION_FAILED: 'Some answers are not valid. Check the highlighted questions and try again.',
  UNAUTHENTICATED: 'Your session ended. Sign in again to continue.',
  PERMISSION_DENIED: 'You do not have permission to do that.',
  NOT_FOUND: 'That record no longer exists, or you cannot see it.',
  INVALID_REFERENCE: 'Something this record points at no longer exists.',
  /** FF-AUDIT-20260914-210459-018: the incident's investigation gate (the page names the fields; this is the toast). */
  INVESTIGATION_INCOMPLETE: 'Record the investigation before moving the incident on.',
  CAR_NOT_VERIFIED: 'The linked corrective action must be verified before this incident can close.',
  SETTING_REQUIRES_OWNER: 'Only the company owner can change this.',
  ADMIN_SCOPE_OVERRIDE_PRESENT: 'This person has their own choice of which records they see. Only the company owner can make them an administrator, or stop them being one, until that choice is cleared.',
  STALE_UPDATE: 'Someone else changed this record while you were editing. Reload to see their version.',
  RATE_LIMITED: 'Too many requests. Wait a moment and try again.',
  FEATURE_DISABLED: 'That feature is switched off for your company.',
  /** FF-AUDIT-060: Take ownership runs the assignment gate; the refusing gate
   * (location, department, qualification) is appended from `assignmentCopy.traceReason`. */
  CHECK_TAKE_NOT_ELIGIBLE: 'You are not eligible to take this check — ask a supervisor to reassign it.',
  /** FF-AUDIT-073: a total below the scrap + rework already recorded cannot be honoured without inventing a split. */
  NCR_COST_BELOW_COMPONENTS: 'That total is less than the scrap and rework costs already recorded. Edit the cost components instead.',
  /** FF-QA-071: separation of duties on the MRB disposition (403). Nothing on the form is wrong, so it must not read as a validation failure. */
  NCR_SELF_DISPOSITION: 'You raised this nonconformance, so someone else must disposition it.',
  /** FF-AUDIT-022: an HR draft issued before its required form is filled in. */
  FORM_REQUIRED: 'Fill in the record’s form before issuing it.',
  FORM_MISMATCH: 'This record’s form is not the one its type now requires. Fill in the current form before issuing.',
  /** FF-AUDIT-037: claim/reassign on a task that is done, completed, closed, cancelled or missed. */
  TASK_TERMINAL: 'This work is finished, so its assignment can no longer change.',
  /** F19: two people took the same work request; this one was second. */
  REQUEST_ALREADY_TAKEN: 'Someone else has already taken this request.',
  /** F19: a request with no machine cannot become a work order until a supervisor triages it. */
  REQUEST_NEEDS_ASSET: 'This request names no machine, so it cannot be taken yet — a supervisor must triage it.',
  /** F5.3: a step whose photo mode is Required, ticked without a photo. */
  STEP_PHOTO_REQUIRED: 'This step needs a photo before it can be marked done.',
  /** F6: "Is the machine still running?" on work that has no machine, or on a check that did not fail. */
  EQUIPMENT_STATE_NOT_APPLICABLE: 'Only failed work on a machine can say whether the machine is still running.',
  /** F6: the "stopped since" time is in the future or more than a day before the work began. */
  STOPPED_AT_INVALID: 'The time the machine stopped cannot be in the future or more than a day before the work began.',
  /** FF-AUDIT-081 + owner decision 2026-09-28: a retired asset takes no new plans, work orders or downtime, and its paused plans cannot be resumed. */
  EQUIPMENT_RETIRED: 'This asset is retired, so it cannot take new or resumed plans, work orders or downtime.',
  /** Owner decision 2026-09-28: archiving pauses an asset's plans; no new plan, resume or generation until it is restored. */
  EQUIPMENT_ARCHIVED: 'This asset is archived, so it cannot take new maintenance plans and its plans cannot be resumed or raise work orders. Restore the asset first.',
  /** Owner decision 2026-09-28: two requests generated the same plan occurrence at once; the other one's work order stands. */
  PLAN_ALREADY_GENERATED: 'A work order for this plan’s current due date was just created. Find it on the Work orders tab.',
  /** FF-AUDIT-075: a release on material that is no longer held. */
  NC_NOT_ON_HOLD: 'This material is not on hold — nothing to release.',
  /** FF-AUDIT-010: submitted for verification with checklist actions still open. */
  CAR_ACTIONS_OPEN: 'Some checklist actions are still open. Tick them off, or say why they do not block verification.',
  CAR_ANALYSIS_INCOMPLETE: 'Record the root cause and at least one action (or why none is needed) before submitting for verification.',
  /** FF-AUDIT-20260914-210459-095: a non-HR caller files records for their own department only. */
  HR_NO_ESCALATION_LEVEL: 'No record type escalates from this one. Add an "Escalates from" ladder in Settings → HR record types if it should.',
  HR_SUBJECT_OUT_OF_CHAIN: 'You can file HR records only for the workers in your own department. HR designates can file for anyone.',
  FMEA_ACTIVE_READ_ONLY: 'This revision is active and its analysis is read-only. Start a new revision to change it.',
  FMEA_REVISION_IN_PROGRESS: 'A newer revision of this worksheet is already in progress as a draft. Open that draft instead.',
  DISTRIBUTION_RECIPIENTS_NO_ACCESS: 'Some of the chosen recipients cannot open this document. Grant them access first, or choose other recipients.',
  DISTRIBUTION_NOTHING_TO_RESEND: 'Everyone on this distribution was reached. There is nobody to resend to.',
  DISTRIBUTION_RESEND_IN_PROGRESS: 'This distribution is already being resent. Wait a minute, then check its status.',
  DISTRIBUTION_SUPERSEDED: 'This document has a newer version. Distribute the current version instead.',
  TWO_FACTOR_ENROLLMENT_REQUIRED: 'Set up two-factor authentication before continuing.',
  EMAIL_VERIFICATION_REQUIRED: 'Verify your email address before continuing.',
  /** Platform Console plan: FloorFile has not licensed this module for the company. */
  MODULE_NOT_ENTITLED: 'Your company’s subscription does not include this feature.',
  /** The company's commercial account is restricted (lapsed trial, cancelled or paused subscription). */
  ACCOUNT_RESTRICTED: 'Your company’s subscription is not active, so this feature is unavailable. Your records are kept.',
  /** A commercial limit (facilities, assets…) has been reached. */
  ENTITLEMENT_LIMIT_REACHED: 'Your company has reached its subscription’s limit for this.',
  COMPANY_SUSPENDED: 'This company account is suspended. Contact your administrator.',
  COMPANY_ARCHIVED: 'This company account is archived.',
  COMPANY_PURGED: 'This company account has been deleted.',
  COMPANY_DELETE_DISABLED: 'Deleting a company is switched off.',
  IDEMPOTENCY_REPLAY_MISMATCH: 'This request was already sent with different details.',
  STORAGE_UNAVAILABLE: 'File storage is unavailable right now. Your other changes were not affected.',
  DB_UNAVAILABLE: 'The service is unavailable right now. Nothing was saved.',
  /** FF-AUDIT-20260914-210459-096: a bodiless 5xx — the dead-zone answer, not the API. */
  UNREACHABLE: 'The server could not be reached. You may be offline; work saved on this device is kept and sent when the connection returns.',
  DB_TIMEOUT: 'That took too long and was stopped. Nothing was saved.',
  INTERNAL: 'Something went wrong on our side. Nothing was saved.',

  // Data governance (D26). These are refusals, not faults — each one says what
  // rule applied and what to do instead, because "you cannot" with no reason is
  // indistinguishable from a bug.
  RECORD_LOCKED: 'This record is locked and can no longer be edited.',
  LEGAL_HOLD: 'This record is under legal hold and cannot be changed or deleted.',
  RETENTION_FLOOR: 'This record is inside its retention period and cannot be deleted yet.',
  EDIT_REASON_REQUIRED: 'A reason is required for this change.',
  USE_CORRECTION_PATH: 'This record is closed. Record a correction instead of editing it.',
  /** Owner decision 2026-09-16 (round 2): requester ≠ approver on deviations / OOS. */
  DEVIATION_SELF_APPROVAL: 'You raised this deviation, so someone else must approve it.',
  /** FF-QA-084 (owner decision 2026-09-27): approving and raising a planned deviation are management's call. */
  DEVIATION_APPROVER_REQUIRED: 'Only management or a named approver can approve a deviation.',
  DEVIATION_PLANNED_SUBMITTER_REQUIRED: 'Only management or a named planned-deviation submitter can raise a planned deviation. Record it as unplanned instead.',
  OOS_SELF_CONCLUSION: 'You opened this investigation, so someone else must conclude it.',
  /** FF-AUDIT-073 */
  ATTENDANCE_RANGE_REVERSED: 'The end date must be on or after the start date.',
  /** F15: floorplans. */
  MAP_NAME_TAKEN: 'Another floorplan in this facility already has that name.',
  MAP_ARCHIVED: 'This floorplan was deleted. Restore it before changing it.',
  MAP_IMAGE_INVALID: 'That file is not an image uploaded to this floorplan. Upload the drawing again.',
  MAP_PIN_CONFLICT: 'That asset is already pinned to this floorplan.',
  /** F18: two record types can never print the same number. */
  NUMBER_PREFIX_TAKEN: 'Another record type already uses that prefix. Each type needs its own.',
  /** F23 */
  STANDARD_EXISTS: 'A standard already starts that month for this facility. Edit it instead.',
  /** F1: the asset tree and linked equipment. */
  INVALID_HIERARCHY: 'That cannot be its parent: it would make a loop, sit in another facility, or use an archived asset.',
  HIERARCHY_TOO_LARGE: 'Too many assets to show as a tree. Use the table, or narrow to one facility.',
  LINK_EXISTS: 'These two assets are already linked that way.',
  LINK_IS_HIERARCHY: 'That asset is already its parent or sub-asset. Links are for assets outside the assembly.',
  LINK_SELF: 'An asset cannot be linked to itself.',
  ASSIGNMENT_NOT_ALLOWED: 'Your assignment rules do not let you give this work to that person. You can still escalate it.',
  NO_ESCALATION_TARGET: 'There is nobody above you to escalate this to at this facility.',
  OWNER_RULES_LOCKED: 'The owner may always assign anyone; there is nothing to set.',
  TEAM_NAME_TAKEN: 'Another team already has that name.',
  TEAM_FACILITY_CONFLICT: 'This team answers for equipment at another facility. Change those assets first.',
  RESPONSIBLE_TEAM_ARCHIVED: 'That team is archived. Restore it, or choose another.',
  TEAM_ARCHIVED: 'That team is archived. Restore it, or choose another.',
  TEAM_OTHER_FACILITY: 'That team works at another facility. Choose a team from this one, or a company-wide team.',
  /** Tenancy review of F8.3 — a team cannot be moved away from, or archived with, work nobody else could take. */
  TEAM_HAS_WORK_ELSEWHERE: 'This team still has work at another facility. Give that work to someone else first.',
  TEAM_HAS_OPEN_WORK: 'This team still has open work or plans. Give them to someone else first.',
  TEMPLATE_ALREADY_INSTALLED: 'That library template is already in use here.',
  TEMPLATE_NOT_FOUND: 'That library template does not exist.',
  WORK_TRANSFER_SAME_PERSON: 'Choose someone other than the person whose work you are moving.',
  WORK_TRANSFER_TARGET_INVALID: 'That person cannot receive work in this company.',
  WORK_TRANSFER_ALREADY_RETURNED: 'This transfer has already been sent back.',
  RESPONSIBLE_TEAM_OTHER_FACILITY: 'That team works at another facility.',
  RESPONSIBLE_USER_NOT_AT_FACILITY: 'That person cannot be given work at this asset’s facility.',
  UNKNOWN_TEAM: 'One of those teams does not exist or is archived.',
  HISTORY_ACCESS_DENIED: 'Your company has not given your role or team access to change history.',

  // F24B — telemetry sources (Sensors & SCADA). SIGNATURE_* / IP_NOT_ALLOWED /
  // PAYLOAD_* / SOURCE_* / READING_QUOTA_EXCEEDED are refusals a PLANT SYSTEM'S
  // request hits, shown as a one-line sentence in the Activity tab's batch
  // ring; FACILITY_MISMATCH / METRIC_UNKNOWN / DUPLICATE_TAG / EQUIPMENT_UNKNOWN
  // are thrown from the tag-mapping dialog and its CSV import.
  SIGNATURE_REQUIRED: 'This source requires a signed request, and none was sent.',
  SIGNATURE_INVALID: 'The request’s signature does not match this source’s secret.',
  SIGNATURE_EXPIRED: 'The request’s signature is more than 5 minutes old.',
  IP_NOT_ALLOWED: 'The request did not come from an address on this source’s allow-list.',
  PAYLOAD_TOO_LARGE: 'The request was over the 1 MB limit.',
  PAYLOAD_UNPARSEABLE: 'The request body could not be read for this source’s kind.',
  SOURCE_DISABLED: 'This source is disabled and refused the request.',
  SOURCE_KEY_MISMATCH: 'That API key does not belong to this source.',
  READING_QUOTA_EXCEEDED: 'The company’s daily reading limit was reached.',
  FACILITY_MISMATCH: 'That equipment is not at this source’s facility.',
  METRIC_UNKNOWN: 'That metric does not exist, or is archived.',
  DUPLICATE_TAG: 'Another row in this import already maps that external tag.',
  EQUIPMENT_UNKNOWN: 'That equipment does not exist, or is archived.',
  /** Thrown by the platform auth middleware, not the ingest ring — a
   * telemetry source's own scoped key was used against a different endpoint. */
  SOURCE_KEY_NOT_ALLOWED: 'This key belongs to a telemetry source and can only push readings to it.',
  SOURCE_ARCHIVED: 'An archived source can’t be given a new key.',
  /** `METRICS_ERROR_CODES` — thrown from the metric-definition editor and a
   * maintenance plan's meter-following setup. */
  METRIC_KEY_TAKEN: 'A metric with that key already exists.',
  METRIC_NOT_COUNTER: 'A meter-based plan can only follow a counter metric (hours, cycles) — not a gauge.',

  // F2 — "Fill from photo" and other direct AI calls (the panel's own
  // `unavailable` reasons in features/ai/copy.ts cover the render-gate case;
  // these are for a request that was SENT and refused).
  AI_DISABLED: 'AI features are switched off for this company.',
  AI_VISION_UNAVAILABLE: 'The configured AI provider cannot read images yet. Ask an admin to enable it under AI → Providers.',
  AI_BUDGET_EXCEEDED: 'The monthly AI budget is reached. AI features pause until it is raised or the month resets.',
  AI_OUTPUT_INVALID: 'The AI could not produce a usable answer. Try again, or enter the details by hand.',
  AI_TIMEOUT: 'The AI provider took too long to answer. Try again.',
})

export type ErrorCopyCatalog = typeof errorCopy.en
